import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { decisionOutcomeCopy, type WebLedgerDecision } from '../../../lib/goal-ledger';
import {
  DECISION_ANSWER_COUNTER_AT,
  DECISION_ANSWER_LIMIT,
  decisionControl,
  decisionDetailText,
  decisionOptions,
  isAnswerable,
  type DecisionAnswer,
} from '../../../lib/decisions';
import { useDecisionAnswer, type DecisionAnswerFlow } from '../../../lib/use-decision-answer';
import { Button, TextButton } from '../ui';
import { UpgradeButton } from '../connectors/primitives';
import { connectOptionToolkit } from '../../../lib/plan-review';
import { toolkitDisplayName } from '../../../lib/dashboard-error-kind';
import { cachedToolkitName, useToolkitConnect } from '../../../lib/use-toolkit-connect';
import styles from './DecisionAnswerBlock.module.css';

/**
 * The ANSWER to an open question, inside the row that asks it.
 *
 * A decision has always been readable here and answerable nowhere — the only route was typing in chat,
 * where the agent had to infer what you meant and could not cast the verdict anyway. This is the control
 * that closes that, and every shape it takes is `docs/answering-a-decision-design.md`'s, because the two
 * phones are building the identical block from the same document.
 *
 * What is worth knowing before changing it:
 *
 * - **There is no confirmation step, in either direction.** The question is the prompt — it is authored
 *   as one sentence the reader can answer and it sits directly above the buttons. A dialog on every
 *   approval is answered without reading inside a week, and it would then be worse than nothing. The
 *   mis-answer this feature actually risks is a mis-TAP, which a dialog does not address: the targets,
 *   the spacing and the weights do, and the row itself is NOT tappable — only its controls are.
 * - **Refusing is not painted red.** Turning something down is an ordinary answer that the plan obeys,
 *   and colouring it as damage pushes people toward approving things they have not read. Every kind can
 *   be refused, including a CHOOSE and an ANSWER, because `resolution` is orthogonal to kind in the
 *   service — that is what makes "no" legible to a plan that does not know the question's vocabulary.
 * - **Tapping a CHOOSE option IS the answer.** No pre-selection, no second Submit. A "selected but not
 *   sent" state reads as answered and is not, which is the exact failure this feature exists to remove.
 * - **An answered row is replaced in place by the settled sentence** — the same sentence it will carry
 *   in "What happened" forever after. No toast, no undo affordance: there is no undo. `CastVerdict` is
 *   the only path to SETTLED and a second verdict on a settled row is refused, by anything.
 * - **The row never claims work started.** The release is asynchronous, at-least-once and unbounded, and
 *   a released task may still be held by another decision. The plan re-reads and shows what is true.
 */
export function DecisionAnswerBlock({
  decision,
  goalId,
  parentGoalId,
  questionId,
  index,
}: {
  decision: WebLedgerDecision;
  goalId: string | undefined;
  parentGoalId: string | undefined;
  /** The question's element id — the group is labelled by it, so a reader knows what it answers. */
  questionId: string;
  index: number;
}) {
  const flow = useDecisionAnswer(decision, goalId, parentGoalId);
  return <DecisionAnswerControls decision={decision} questionId={questionId} index={index} flow={flow} />;
}

/**
 * The same control, with its answering state HANDED IN rather than owned.
 *
 * The task row renders this one. A row that carries the question needs to know when the answer landed —
 * it flips its own status chip and says what the reader chose — and the only way for two components to
 * agree about one irreversible act is for there to be ONE flow behind it. A second `useDecisionAnswer`
 * beside the first would be a second plan-version hold and a second verdict pin: two ids for one answer,
 * which is precisely the double vote the pin exists to prevent.
 *
 * So this is where every shape lives and `DecisionAnswerBlock` is the ledger's thin wrapper over it.
 * There is one control on this surface, and both callers render this function.
 */
export function DecisionAnswerControls({
  decision,
  questionId,
  index,
  flow,
  biPage = 'goal',
  biLocation,
  noteOnApprove = false,
}: {
  decision: WebLedgerDecision;
  questionId: string;
  index: number;
  flow: DecisionAnswerFlow;
  /** Which screen the answer was given on, for BI. The ledger is `goal`; the task row says `tasks`. */
  biPage?: string;
  /** Which kind of decision this is, for BI — e.g. `meeting_approval`, so it can be told apart from the rest on `chat`. */
  biLocation?: string;
  /** The note rides Approve too, not only a refusal. Only the meeting approval card sets it: there the
   *  owner's words are what Maestro says to the room. Every other decision keeps the note refuse-only. */
  noteOnApprove?: boolean;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [draft, setDraft] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const settledRef = useRef<HTMLSpanElement | null>(null);

  const control = decisionControl(decision);
  const detail = decisionDetailText(decision);

  // A keyboard or switch user who just pressed Approve is standing on a button that no longer exists.
  // Without this they land back at the page root; the row's new sentence is where they actually are.
  useEffect(() => {
    if (flow.answered) settledRef.current?.focus();
  }, [flow.answered]);

  // ONE note per row, read by whichever refusal control this kind renders (the CHOOSE/ANSWER text
  // button and the APPROVE/CONFIRM Decline are different controls over the same act), and by Approve
  // when `noteOnApprove` is set. Held here rather than inside them so they cannot drift into two notes
  // for one verdict.
  //
  // Declared with the other hooks, ABOVE the two early returns below — a hook after a conditional return
  // runs in a different order on the render where that branch is taken, which is a crash rather than a
  // cosmetic problem.
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');

  // A decision this build cannot read the status of fails INTO the open list on purpose ("we might still
  // need you" is recoverable). It is still not something we may cast a verdict against.
  if (!isAnswerable(decision)) return null;

  if (flow.answered) {
    const copy = decisionOutcomeCopy(flow.answered);
    return (
      <div className={styles.block}>
        <span className={styles.settled} ref={settledRef} tabIndex={-1} aria-live="polite">
          {t(copy.key, copy.params)}
        </span>
      </div>
    );
  }

  /** A refusal, carrying the reader's words when they typed any. Trimmed, and `undefined` rather than
   *  `''` when blank — an empty string is a value the wire would store as an answer nobody wrote. */
  const refusal = (): DecisionAnswer => ({ resolution: 'REFUSED', answer: note.trim() || undefined });
  const approval = (): DecisionAnswer => {
    const words = noteOnApprove ? note.trim() : '';
    return words ? { resolution: 'ACCEPTED', answer: words } : { resolution: 'ACCEPTED' };
  };

  const answer = (controlId: string, biName: string, value: DecisionAnswer) => {
    logPageClickBi({ page_name: biPage, button_name: biName, answer: decision.id, location: biLocation });
    void flow.submit(controlId, value);
  };
  const disabled = flow.busy !== null || flow.effect?.retryable === false;
  const options = decisionOptions(decision);
  const trimmed = draft.trim();
  const remaining = DECISION_ANSWER_LIMIT - draft.length;

  return (
    <div className={styles.block} role="group" aria-labelledby={questionId}>
      {detail && (
        <>
          <TextButton
            size="tiny"
            className={styles.detailToggle}
            onClick={() => setDetailOpen((open) => !open)}
            aria-expanded={detailOpen}
          >
            {t(detailOpen ? 'goals.decision.detailHide' : 'goals.decision.detailShow')}
          </TextButton>
          {detailOpen && <DecisionDetail detail={detail} />}
        </>
      )}
      {/* Always present, on every kind, in the meta weight — a fact, not a warning banner. It is the
          honest, non-blocking half of the care a confirm dialog pretends to be: five words that are
          true of every decision and assert nothing about what this one releases, which is a thing we
          genuinely cannot say. */}
      <span className={styles.final}>{t('goals.decision.final')}</span>

      {control === 'unanswerable' ? (
        // A CHOOSE that arrived with no answerable options is a corrupt row, not an empty picker.
        // Synthesising Approve/Decline for it would send a vocabulary this decision does not have.
        <span className={styles.note}>{t('goals.decision.unanswerable')}</span>
      ) : control === 'choose' ? (
        <div className={styles.options}>
          {options.map((option, optionIndex) => {
            const toolkit = connectOptionToolkit(option);
            return toolkit ? (
              // A hand-back option that CONNECTS an app: it opens the connect flow and sends no answer, as the chip
              // row (TaskDecisionPrompts) and both phones' full controls do.
              <ConnectOptionButton key={option.id} toolkit={toolkit} disabled={disabled} biPage={biPage} decisionId={decision.id} testId={`decisionConnect-${index}-${optionIndex}`} />
            ) : (
            <Button
              key={option.id}
              variant="outlined"
              size="small"
              className={styles.option}
              data-testid={`decisionOption-${index}-${optionIndex}`}
              loading={flow.busy === option.id}
              disabled={disabled}
              onClick={() => answer(option.id, 'answer_decision_choose', { resolution: 'ACCEPTED', choice: option.id })}
            >
              <span className={styles.optionLabel} dir="auto">
                {option.label}
              </span>
            </Button>
            );
          })}
          <RefuseAction
            index={index}
            label={t('goals.decision.chooseNone')}
            busy={flow.busy === 'refuse'}
            disabled={disabled}
            onClick={() => answer('refuse', 'answer_decision_refuse', refusal())}
          />
        </div>
      ) : control === 'answer' ? (
        <div className={styles.answer}>
          <textarea
            className={styles.answerField}
            data-testid={`decisionAnswerField-${index}`}
            value={draft}
            // Truncating is the cap, not an error afterwards: a field with no visible ceiling invites a
            // paste that then fails a wire limit nobody warned about.
            onChange={(event) => setDraft(event.target.value.slice(0, DECISION_ANSWER_LIMIT))}
            placeholder={t('goals.decision.answerPlaceholder')}
            maxLength={DECISION_ANSWER_LIMIT}
            rows={1}
            disabled={disabled}
            dir="auto"
          />
          {remaining <= DECISION_ANSWER_COUNTER_AT && (
            <span className={styles.counter}>{t('goals.decision.answerTooLong', { count: remaining })}</span>
          )}
          <div className={styles.actions}>
            <Button
              size="small"
              data-testid={`decisionSend-${index}`}
              loading={flow.busy === 'send'}
              // The server refuses a blank ACCEPTED answer, and a client that lets you press it and then
              // shows you the server's refusal is a client that made you fail for it.
              disabled={disabled || !trimmed}
              onClick={() => answer('send', 'answer_decision_send', { resolution: 'ACCEPTED', answer: trimmed })}
            >
              {t('goals.decision.send')}
            </Button>
            <RefuseAction
              index={index}
              label={t('goals.decision.answerRefuse')}
              busy={flow.busy === 'refuse'}
              disabled={disabled}
              onClick={() => answer('refuse', 'answer_decision_refuse', refusal())}
            />
          </div>
        </div>
      ) : (
        <div className={styles.actions}>
          <Button
            size="small"
            data-testid={`decisionApprove-${index}`}
            loading={flow.busy === 'approve'}
            disabled={disabled}
            onClick={() => answer('approve', 'answer_decision_approve', approval())}
          >
            {t(control === 'confirm' ? 'goals.decision.confirm' : 'goals.decision.approve')}
          </Button>
          {/* Not destructive-red — see the block note. */}
          <Button
            variant="outlined"
            size="small"
            data-testid={`decisionDecline-${index}`}
            loading={flow.busy === 'refuse'}
            disabled={disabled}
            onClick={() => answer('refuse', 'answer_decision_refuse', refusal())}
          >
            {t(control === 'confirm' ? 'goals.decision.deny' : 'goals.decision.decline')}
          </Button>
        </div>
      )}

      {/* The words a refusal can carry, on every kind — the reader who turns down every option had
          nowhere to say why, or what they would rather have, and the only route was typing it in chat.
          `Verdict.answer` is orthogonal to `resolution` on the wire, so this needs no new field.

          It never gates the refusal: the refuse controls above submit whatever is here, including
          nothing, so a reader who ignores this box refuses in exactly one tap as before. Opening it is
          not a step in front of an answer — which is the same rule the block note states about CHOOSE,
          for the same reason: a control that must be filled in first creates a "typed but not sent"
          state that reads as answered and is not. */}
      {control !== 'unanswerable' && !flow.answered && (
        <div className={styles.refuseNote}>
          {noteOpen ? (
            <textarea
              className={styles.refuseNoteField}
              data-testid={`decisionRefuseNoteField-${index}`}
              value={note}
              onChange={(event) => setNote(event.target.value.slice(0, DECISION_ANSWER_LIMIT))}
              placeholder={t('goals.decision.refuseNotePlaceholder')}
              maxLength={DECISION_ANSWER_LIMIT}
              rows={2}
              disabled={disabled}
              dir="auto"
            />
          ) : (
            <TextButton
              size="small"
              data-testid={`decisionRefuseNote-${index}`}
              disabled={disabled}
              onClick={() => setNoteOpen(true)}
            >
              {t('goals.decision.refuseNote')}
            </TextButton>
          )}
        </div>
      )}

      {flow.effect && (
        <span
          className={flow.effect.tone === 'danger' ? styles.error : styles.note}
          data-testid={`decisionError-${index}`}
          role="alert"
        >
          {t(flow.effect.messageKey)}
        </span>
      )}
      {/* A visual-only state change is invisible to a screen reader that has already read the row. */}
      <span className={styles.announce} aria-live="polite">
        {flow.busy ? t('goals.decision.sending') : ''}
      </span>
    </div>
  );
}

/** The refusal, at a lower weight than the answers — an ordinary answer, not a lesser one and not damage. */
function RefuseAction({
  index,
  label,
  busy,
  disabled,
  onClick,
}: {
  index: number;
  label: string;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <TextButton
      size="small"
      className={styles.refuse}
      data-testid={`decisionRefuse-${index}`}
      disabled={disabled}
      aria-busy={busy || undefined}
      onClick={onClick}
    >
      {label}
    </TextButton>
  );
}

/** The agent's own context, plain and capped — the same treatment every other stretch of ledger prose gets. */
function DecisionDetail({ detail }: { detail: NonNullable<ReturnType<typeof decisionDetailText>> }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <span className={styles.detail} dir="auto">
        {expanded ? detail.full : detail.text}
      </span>
      {detail.truncated && (
        <TextButton size="tiny" className={styles.detailToggle} onClick={() => setExpanded((value) => !value)}>
          {t(expanded ? 'common.collapse' : 'common.expand')}
        </TextButton>
      )}
    </>
  );
}

/** "Connect {app}" for a hand-back option carrying `connect`: runs the Connectors screen's dispatch, never a verdict. */
function ConnectOptionButton({ toolkit, disabled, biPage, decisionId, testId }: { toolkit: string; disabled: boolean; biPage: string; decisionId: string; testId: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { busy, actionError, actionUpgrade, connectToolkit } = useToolkitConnect();
  return (
    <>
      <Button
        variant="outlined"
        size="small"
        className={styles.option}
        data-testid={testId}
        loading={busy.has(toolkit)}
        disabled={disabled}
        onClick={() => {
          logPageClickBi({ page_name: biPage, button_name: 'connect_from_decision', answer: decisionId }, { componentName: toolkit });
          void connectToolkit(toolkit);
        }}
      >
        <span className={styles.optionLabel} dir="auto">
          {t('tasks.planReview.connectApp', { app: cachedToolkitName(toolkit) ?? toolkitDisplayName(toolkit) })}
        </span>
      </Button>
      {actionError ? (
        <span className={styles.note} role="alert">
          {actionError}
          {actionUpgrade && <UpgradeButton />}
        </span>
      ) : null}
    </>
  );
}
